/**
 * Bridge Club QR Analytics
 *
 * Track QR code portal visits:
 * - Visit volume and trends
 * - Performance by placement location
 * - Store-level QR engagement
 * - Conversion rate estimation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import './BridgeClubPages.css';
import './BridgeClubQRAnalytics.css';

interface Summary {
  total_visits: number;
  this_month: number;
  last_month: number;
  last_7_days: number;
  growth_rate: number;
  est_conversion_rate: number;
}

interface PlacementData {
  placement: string;
  visits: number;
  label: string;
}

interface StoreData {
  store_id: number;
  store_name: string;
  visits: number;
}

interface DailyTrend {
  date: string;
  visits: number;
}

interface QRAnalyticsData {
  summary: Summary;
  by_placement: PlacementData[];
  by_store: StoreData[];
  daily_trend: DailyTrend[];
}

const PLACEMENT_COLORS = ['#1a472a', '#059669', '#3b82f6', '#f59e0b', '#8b5cf6', '#6b7280'];

const BridgeClubQRAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<QRAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get<QRAnalyticsData>('/admin/bridge-club/qr-analytics/');
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load QR analytics:', err);
      setError(err.response?.data?.error || 'Failed to load QR analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getPlacementIcon = (placement: string) => {
    switch (placement) {
      case 'floor': return 'F';
      case 'register': return 'R';
      case 'shelf': return 'S';
      case 'signage': return 'G';
      case 'direct': return 'D';
      default: return 'O';
    }
  };

  if (loading) {
    return (
      <div className="bridge-club-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading QR analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bridge-club-page">
        <div className="page-header">
          <div className="header-content">
            <button className="back-button" onClick={() => navigate('/admin/bridge-club/network')}>
              &larr; Back to Network
            </button>
            <h1>QR Analytics</h1>
          </div>
        </div>
        <div className="error-banner">
          {error || 'Failed to load data'}
          <button onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const { summary, by_placement, by_store, daily_trend } = data;

  return (
    <div className="bridge-club-page qr-analytics-page">
      <div className="page-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate('/admin/bridge-club/network')}>
            &larr; Back to Network
          </button>
          <h1>QR Analytics</h1>
          <p>Track portal visits from QR codes across your store network</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-row">
        <div className="stat-card highlight">
          <div className="stat-value">{summary.total_visits.toLocaleString()}</div>
          <div className="stat-label">Total Visits</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary.this_month.toLocaleString()}</div>
          <div className="stat-label">This Month</div>
          {summary.growth_rate !== 0 && (
            <div className={`stat-change ${summary.growth_rate >= 0 ? 'positive' : 'negative'}`}>
              {summary.growth_rate >= 0 ? '+' : ''}{summary.growth_rate}%
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary.last_7_days.toLocaleString()}</div>
          <div className="stat-label">Last 7 Days</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary.est_conversion_rate}%</div>
          <div className="stat-label">Est. Conversion</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Daily Trend */}
        <div className="chart-card wide">
          <h3>Visit Trend (30 Days)</h3>
          {daily_trend.length === 0 ? (
            <div className="no-data">No visit data available</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                />
                <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                <Tooltip
                  formatter={(value: number) => [value, 'Visits']}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Line
                  type="monotone"
                  dataKey="visits"
                  stroke="#1a472a"
                  strokeWidth={2}
                  dot={{ fill: '#1a472a', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Placement Breakdown */}
        <div className="chart-card">
          <h3>By Placement</h3>
          {by_placement.length === 0 ? (
            <div className="no-data">No placement data</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={by_placement as any[]}
                  dataKey="visits"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ name, percent }: { name?: string; percent?: number }) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {by_placement.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={PLACEMENT_COLORS[index % PLACEMENT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => [value, 'Visits']} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Detailed Lists */}
      <div className="detail-row">
        {/* Placement Performance */}
        <div className="data-card">
          <h3>Placement Performance</h3>
          {by_placement.length === 0 ? (
            <div className="no-data-small">No placement data</div>
          ) : (
            <div className="placement-list">
              {by_placement.map((p, index) => {
                const totalVisits = by_placement.reduce((sum, item) => sum + item.visits, 0);
                const percentage = totalVisits > 0 ? (p.visits / totalVisits * 100).toFixed(1) : 0;
                return (
                  <div key={p.placement} className="placement-item">
                    <div
                      className="placement-icon"
                      style={{ backgroundColor: PLACEMENT_COLORS[index % PLACEMENT_COLORS.length] }}
                    >
                      {getPlacementIcon(p.placement)}
                    </div>
                    <div className="placement-info">
                      <span className="placement-label">{p.label}</span>
                      <div className="placement-bar">
                        <div
                          className="placement-bar-fill"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: PLACEMENT_COLORS[index % PLACEMENT_COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                    <div className="placement-stats">
                      <span className="placement-visits">{p.visits.toLocaleString()}</span>
                      <span className="placement-pct">{percentage}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Stores */}
        <div className="data-card">
          <h3>Top Stores by QR Visits</h3>
          {by_store.length === 0 ? (
            <div className="no-data-small">No store data</div>
          ) : (
            <div className="stores-list">
              {by_store.map((store, index) => (
                <div
                  key={store.store_id}
                  className="store-item clickable"
                  onClick={() => navigate(`/admin/bridge-club/stores/${store.store_id}`)}
                >
                  <div className="store-rank">#{index + 1}</div>
                  <div className="store-info">
                    <span className="store-name">{store.store_name}</span>
                  </div>
                  <span className="store-visits">{store.visits.toLocaleString()} visits</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tips Section */}
      <div className="tips-card">
        <h3>QR Optimization Tips</h3>
        <div className="tips-grid">
          <div className="tip-item">
            <div className="tip-icon">1</div>
            <div className="tip-content">
              <strong>Register Placement</strong>
              <p>QR codes at the register have the highest scan rates due to customer wait time.</p>
            </div>
          </div>
          <div className="tip-item">
            <div className="tip-icon">2</div>
            <div className="tip-content">
              <strong>Clear Call-to-Action</strong>
              <p>Add "Scan for Rewards" or "Join the Bridge Club" text near QR codes.</p>
            </div>
          </div>
          <div className="tip-item">
            <div className="tip-icon">3</div>
            <div className="tip-content">
              <strong>Product Shelf Tags</strong>
              <p>Place QR codes on frequently purchased products for targeted engagement.</p>
            </div>
          </div>
          <div className="tip-item">
            <div className="tip-icon">4</div>
            <div className="tip-content">
              <strong>Track Performance</strong>
              <p>Review placement analytics weekly to optimize QR code positioning.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BridgeClubQRAnalytics;
